/**
 * Контекст блокировки экрана: unlock token только в памяти React.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { securityApi, type UserSecurityMe } from "@/services/security.service";
import { getToken } from "@/lib/api-client-v2";
import { useIdleTimer } from "@/hooks/useIdleTimer";
import { ScreenLockOverlay } from "@/components/security/ScreenLockOverlay";
import { screenLockGate } from "@/lib/screen-lock-gate";

type ScreenLockContextValue = {
  locked: boolean;
  unlockToken: string | null;
  /** Заблокировать экран (no-op, если PIN не настроен / модуль выключен). */
  lock: () => void;
  /**
   * Попытка ручной блокировки (кнопка замка в хедере).
   * Возвращает причину отказа или null при успехе.
   */
  tryLock: () => "module_disabled" | "pin_not_configured" | null;
  unlockWithToken: (token: string) => void;
  clearUnlock: () => void;
  me: UserSecurityMe | undefined;
  /** Глобальный флаг модуля screen-lock. */
  screenLockModuleEnabled: boolean;
  /** Idle + ручной lock доступны (модуль + PIN у пользователя). */
  screenLockEnabled: boolean;
};

const ScreenLockContext = createContext<ScreenLockContextValue | null>(null);

export function ScreenLockProvider({ children }: { children: React.ReactNode }) {
  const hasToken = !!getToken();
  const [locked, setLocked] = useState(false);
  const [unlockToken, setUnlockToken] = useState<string | null>(null);
  const lastActivityAt = useRef(Date.now());

  const { data: me } = useQuery({
    queryKey: ["security", "me"],
    queryFn: () => securityApi.me(),
    enabled: hasToken,
    staleTime: 30_000,
    retry: false,
  });

  const { data: status } = useQuery({
    queryKey: ["security", "status"],
    queryFn: () => securityApi.status(),
    enabled: hasToken,
    staleTime: 60_000,
    retry: false,
  });

  const screenLockModuleEnabled = Boolean(status?.screen_lock_module_enabled);
  const screenLockEnabled = Boolean(
    screenLockModuleEnabled && me?.screen_lock_enabled && me?.pin_configured
  );
  const idleMs = Math.max(
    (me?.effective_idle_timeout_seconds ?? status?.idle_timeout_min_seconds ?? 300) * 1000,
    (status?.idle_timeout_min_seconds ?? 60) * 1000
  );

  const lock = useCallback(() => {
    if (!screenLockEnabled) return;
    setLocked(true);
    setUnlockToken(null);
    screenLockGate.setUnlockToken(null);
    screenLockGate.setLocked(true);
  }, [screenLockEnabled]);

  const tryLock = useCallback((): "module_disabled" | "pin_not_configured" | null => {
    if (!screenLockModuleEnabled) return "module_disabled";
    if (!me?.pin_configured || !me?.screen_lock_enabled) return "pin_not_configured";
    setLocked(true);
    setUnlockToken(null);
    screenLockGate.setUnlockToken(null);
    screenLockGate.setLocked(true);
    return null;
  }, [screenLockModuleEnabled, me?.pin_configured, me?.screen_lock_enabled]);

  const unlockWithToken = useCallback((token: string) => {
    setUnlockToken(token);
    screenLockGate.setUnlockToken(token);
    setLocked(false);
    screenLockGate.setLocked(false);
    lastActivityAt.current = Date.now();
  }, []);

  const clearUnlock = useCallback(() => {
    setUnlockToken(null);
    screenLockGate.setUnlockToken(null);
  }, []);

  useIdleTimer({
    enabled: hasToken && screenLockEnabled && !locked,
    timeoutMs: idleMs,
    onIdle: lock,
  });

  // PWA / tab resume
  useEffect(() => {
    if (!hasToken || !screenLockEnabled) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const elapsed = Date.now() - lastActivityAt.current;
      if (elapsed >= idleMs) lock();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [hasToken, screenLockEnabled, idleMs, lock]);

  useEffect(() => {
    if (!locked) lastActivityAt.current = Date.now();
  }, [locked]);

  const value = useMemo(
    () => ({
      locked,
      unlockToken,
      lock,
      tryLock,
      unlockWithToken,
      clearUnlock,
      me,
      screenLockModuleEnabled,
      screenLockEnabled,
    }),
    [
      locked,
      unlockToken,
      lock,
      tryLock,
      unlockWithToken,
      clearUnlock,
      me,
      screenLockModuleEnabled,
      screenLockEnabled,
    ]
  );

  return (
    <ScreenLockContext.Provider value={value}>
      {children}
      {locked && screenLockEnabled ? (
        <ScreenLockOverlay onUnlocked={unlockWithToken} />
      ) : null}
    </ScreenLockContext.Provider>
  );
}

export function useScreenLock(): ScreenLockContextValue {
  const ctx = useContext(ScreenLockContext);
  if (!ctx) {
    throw new Error("useScreenLock must be used within ScreenLockProvider");
  }
  return ctx;
}
